import { useEffect, useMemo, useRef } from "react";

import {
  isQueuedMessageDue,
  useQueuedMessageStore,
  type QueuedComposerMessage,
} from "../../queuedMessageStore";
import type { SessionPhase } from "../../types";

const EMPTY_QUEUE: QueuedComposerMessage[] = [];

export interface QueuedTurnQueueContext {
  readonly threadKey: string;
  readonly phase: SessionPhase;
  readonly latestToolActivityId: string | null;
}

/**
 * The queue's dispatch side: the live messages for the active thread, the
 * due-release effect, and the row handlers.
 *
 * A queued message is due once a tool call finished after it was queued, or
 * the turn ended; the message's own snapshot then goes out through onSend
 * without touching the composer. A pending approval or question blocks the
 * agent, so a queued send would answer nothing and the queue holds until the
 * user resolves it. Steering sends the head message immediately under the
 * same rules; removing returns nothing (it is simply gone).
 */
export function useQueuedTurnDispatch(input: {
  readonly queueContext: QueuedTurnQueueContext | undefined;
  readonly blockedByPendingRequest: boolean;
  /** State mirror of the send-in-flight ref: clearing it re-renders, so a due release retries after a submission ends. */
  readonly isSendInFlight: boolean;
  readonly onSend: (
    event?: { readonly preventDefault: () => void },
    queued?: QueuedComposerMessage,
  ) => Promise<void>;
}): {
  readonly queuedMessages: ReadonlyArray<QueuedComposerMessage>;
  readonly onSteerQueuedMessage: (id: string) => void;
  readonly onRemoveQueuedMessage: (id: string) => void;
} {
  const threadKey = input.queueContext?.threadKey;
  const phase = input.queueContext?.phase;
  const latestToolActivityId = input.queueContext?.latestToolActivityId ?? null;

  const queuedMessages = useQueuedMessageStore((state) =>
    threadKey ? (state.queuesByThreadKey[threadKey] ?? EMPTY_QUEUE) : EMPTY_QUEUE,
  );

  const nextQueuedMessage = queuedMessages[0] ?? null;
  const nextIsDue = useMemo(
    () =>
      nextQueuedMessage !== null &&
      phase !== undefined &&
      isQueuedMessageDue({ message: nextQueuedMessage, phase, latestToolActivityId }),
    [nextQueuedMessage, phase, latestToolActivityId],
  );

  const onSend = input.onSend;
  const blockedByPendingRequest = input.blockedByPendingRequest;
  const isSendInFlight = input.isSendInFlight;
  // The latest onSend behind a ref: the release effect stays on stable
  // dependencies while still calling the fresh closure.
  const onSendRef = useRef(onSend);
  onSendRef.current = onSend;
  useEffect(() => {
    if (!nextIsDue || blockedByPendingRequest || isSendInFlight || nextQueuedMessage === null) {
      return;
    }
    void onSendRef.current(undefined, nextQueuedMessage);
  }, [nextIsDue, blockedByPendingRequest, isSendInFlight, nextQueuedMessage]);

  /** Immediately submits a message only if it belongs to the active provider queue. */
  const onSteerQueuedMessage = (id: string) => {
    const message = queuedMessages.find((entry) => entry.id === id);
    if (!message) return;
    void onSend(undefined, message);
  };

  /** Removes a message from the active provider queue without changing the composer. */
  const onRemoveQueuedMessage = (id: string) => {
    if (!threadKey) return;
    useQueuedMessageStore.getState().remove(threadKey, id);
  };

  return { queuedMessages, onSteerQueuedMessage, onRemoveQueuedMessage };
}
